// UltraFlex Admin Portal - Dashboard
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Users, CreditCard, DoorOpen, ShoppingBag, TrendingUp, TrendingDown,
  AlertTriangle, ChevronRight, Building2, Activity, FileSpreadsheet, FileText, Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, token } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [siteComparison, setSiteComparison] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState('');
  const [sites, setSites] = useState([]);

  useEffect(() => {
    fetchData();
  }, [selectedSite]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch sites
      const sitesRes = await fetch(`${API_URL}/api/sites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const sitesData = await sitesRes.json();
      setSites(sitesData.items || []);

      // Fetch dashboard metrics
      const metricsUrl = selectedSite 
        ? `${API_URL}/api/reporting/dashboard?site_id=${selectedSite}`
        : `${API_URL}/api/reporting/dashboard`;
      
      const metricsRes = await fetch(metricsUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const metricsData = await metricsRes.json();
      setMetrics(metricsData);

      // Fetch site comparison
      const compRes = await fetch(`${API_URL}/api/reporting/site-comparison`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const compData = await compRes.json();
      setSiteComparison(Array.isArray(compData) ? compData : []);

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = "red" }) => (
    <div className="bg-uf-surface rounded-lg p-6 border border-uf-border">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg bg-${color}-500/10`}>
          <Icon className={`w-6 h-6 text-${color}-500`} />
        </div>
        {trend && (
          <div className={`flex items-center text-sm ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
            {trend === 'up' ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
            {trendValue}
          </div>
        )}
      </div>
      <h3 className="text-uf-text-muted text-sm mb-1">{title}</h3>
      <p className="text-3xl font-bold text-uf-text">{value}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-uf-black p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-uf-red"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-uf-black">
      {/* Header */}
      <div className="bg-uf-surface border-b border-uf-border px-4 md:px-8 py-4 md:py-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-uf-text">Admin Dashboard</h1>
            <p className="text-uf-text-muted mt-1 text-sm">
              Welcome back, {user?.first_name}
            </p>
          </div>
          <div className="flex items-center space-x-2 md:space-x-3 w-full sm:w-auto">
            <a
              href="/UltraFlex_Functional_Overview.pdf"
              download
              data-testid="download-functional-overview-btn"
              title="Download stakeholder functional overview (.pdf) — full platform summary"
              className="flex items-center gap-2 px-3 md:px-4 py-2 bg-uf-surface-hi border border-uf-border hover:border-uf-red text-uf-text rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap"
            >
              <FileText className="w-4 h-4" />
              <span className="hidden md:inline">Overview</span>
              <Download className="w-3.5 h-3.5" />
            </a>
            <a
              href="/UltraFlex_Test_Plan_New_Features.xlsx"
              download
              data-testid="download-test-plan-btn"
              title="Download new-features test plan (.xlsx) — 121 tests across 9 sheets"
              className="flex items-center gap-2 px-3 md:px-4 py-2 bg-uf-surface-hi border border-uf-border hover:border-uf-red text-uf-text rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap"
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span className="hidden md:inline">Test Plan</span>
              <Download className="w-3.5 h-3.5" />
            </a>
            <a
              href="/UltraFlex_Gap_Analysis.xlsx"
              download
              data-testid="download-gap-analysis-btn"
              title="Download system-wide gap analysis workbook (.xlsx)"
              className="flex items-center gap-2 px-3 md:px-4 py-2 bg-uf-red hover:bg-uf-red/90 text-white rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap"
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span className="hidden sm:inline">Gap Analysis</span>
              <Download className="w-3.5 h-3.5" />
            </a>
            <select 
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 md:px-4 py-2 text-uf-text text-sm focus:ring-2 focus:ring-uf-red focus:outline-none flex-1 sm:flex-initial"
            >
              <option value="">All Sites (Estate)</option>
              {sites.map(site => (
                <option key={site.id} value={site.id}>{site.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 md:p-6 lg:p-8">
        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard 
            title="Active Members" 
            value={metrics?.active_members?.toLocaleString() || 0}
            icon={Users}
            color="blue"
          />
          <StatCard 
            title="New Joins (30d)" 
            value={metrics?.new_joins || 0}
            icon={TrendingUp}
            trend="up"
            trendValue="+12%"
            color="green"
          />
          <StatCard 
            title="Net Revenue (30d)" 
            value={`£${metrics?.net_revenue?.toLocaleString() || 0}`}
            icon={CreditCard}
            color="purple"
          />
          <StatCard 
            title="Access Today" 
            value={metrics?.access_attempts || 0}
            icon={DoorOpen}
            color="cyan"
          />
        </div>

        {/* Secondary Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <StatCard 
            title="Cancellations (30d)" 
            value={metrics?.cancellations || 0}
            icon={TrendingDown}
            color="orange"
          />
          <StatCard 
            title="Access Denied" 
            value={metrics?.access_denied || 0}
            icon={AlertTriangle}
            color="red"
          />
          <StatCard 
            title="E-commerce Revenue" 
            value={`£${metrics?.ecommerce_revenue?.toLocaleString() || 0}`}
            icon={ShoppingBag}
            color="pink"
          />
        </div>

        {/* Site Comparison Table */}
        <div className="bg-uf-surface rounded-lg border border-uf-border overflow-hidden">
          <div className="px-6 py-4 border-b border-uf-border flex items-center justify-between">
            <h2 className="text-lg font-semibold text-uf-text flex items-center">
              <Building2 className="w-5 h-5 mr-2 text-uf-red" />
              Site Performance Comparison
            </h2>
            <a href="/admin/reports" className="text-uf-red hover:text-uf-red-hover text-sm flex items-center">
              View Full Reports <ChevronRight className="w-4 h-4 ml-1" />
            </a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-uf-surface-hi">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase tracking-wider">Site</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase tracking-wider">Active Members</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase tracking-wider">New Joins</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase tracking-wider">Cancellations</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase tracking-wider">Churn %</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase tracking-wider">Net Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-uf-border">
                {siteComparison.map((site, index) => (
                  <tr key={site.site_id} className="hover:bg-uf-surface-hi transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <span className="text-uf-red font-mono text-sm mr-3">#{index + 1}</span>
                        <div>
                          <p className="text-uf-text font-medium">{site.site_name}</p>
                          <p className="text-uf-text-muted text-xs">{site.site_code}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-uf-text">{site.active_members}</td>
                    <td className="px-6 py-4 text-right text-green-500">+{site.new_joins}</td>
                    <td className="px-6 py-4 text-right text-orange-500">{site.cancellations}</td>
                    <td className="px-6 py-4 text-right">
                      <span className={`${site.churn_rate > 5 ? 'text-red-500' : 'text-uf-text'}`}>
                        {site.churn_rate?.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-uf-text font-medium">£{site.net_revenue?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          <QuickActionCard 
            title="Member Search"
            description="Find and manage members"
            href="/admin/members"
            icon={Users}
          />
          <QuickActionCard 
            title="Exceptions Queue"
            description="Failed payments requiring attention"
            href="/admin/billing/exceptions"
            icon={AlertTriangle}
            badge={metrics?.arrears_count}
          />
          <QuickActionCard 
            title="Access Logs"
            description="View entry attempts"
            href="/admin/access"
            icon={DoorOpen}
          />
          <QuickActionCard 
            title="Analytics"
            description="Occupancy & utilization"
            href="/admin/reports"
            icon={Activity}
          />
        </div>
      </div>
    </div>
  );
};

const QuickActionCard = ({ title, description, href, icon: Icon, badge }) => (
  <a 
    href={href}
    className="bg-uf-surface rounded-lg p-5 border border-uf-border hover:border-uf-red/50 transition-all group"
  >
    <div className="flex items-start justify-between mb-3">
      <Icon className="w-6 h-6 text-uf-red" />
      {badge > 0 && (
        <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
          {badge}
        </span>
      )}
    </div>
    <h3 className="text-uf-text font-semibold group-hover:text-uf-red transition-colors">{title}</h3>
    <p className="text-uf-text-muted text-sm mt-1">{description}</p>
  </a>
);

export default AdminDashboard;
